import React, { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery, useMutation } from 'react-query';
import { useSnackbar } from 'notistack';
import { HomeownerPortalWrapper } from '../components/homeowner-portal-wrapper';
import { homeownerPortalTheme } from '../styles/homeowner-portal-theme';
import partOfOurStoryImage from '../../../assets/homeowner-portal/our_story_hero.webp';
import { HeroSection } from '../components/hero-section';
import { HubspotFormRenderer } from '../components/hubspot-form/hubspot-form-renderer';
import { SuccessModal } from '../components/hubspot-form/success-modal';
import { getHubspotPhotoUploadForm, submitHubspotPhotoUploadForm } from '../../../fetch';
import { IHubspotForm, HubspotFormField } from '../../../models';

export const SubmitPhotos = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const formikRef = useRef<any>(null);

  const {
    data: form,
    isLoading,
    isError,
    error
  } = useQuery<IHubspotForm>(['hubspotForm', 'photoUpload'], getHubspotPhotoUploadForm);

  const submitMutation = useMutation(
    (values: HubspotFormField[]) => submitHubspotPhotoUploadForm(values),
    {
      onSuccess: () => {
        setShowSuccessModal(true);
      },
      onError: () => {
        enqueueSnackbar('Error submitting form. Please try again.', {
          variant: 'error'
        });
      }
    }
  );

  const handleSubmit = (values: Record<string, any>) => {
    if (!form) return;

    // Transform values into array of { objectTypeId, name, value }
    const formattedFields: HubspotFormField[] = [];
    form.fieldGroups.forEach(group => {
      group.fields.forEach(field => {
        formattedFields.push({
          objectTypeId: field.objectTypeId,
          name: field.name,
          value: values[field.name]
        });
      });
    });
    submitMutation.mutate(formattedFields);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Reset the form to clear all fields
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  return (
    <HomeownerPortalWrapper title="Submit Your Photos">
      <Box sx={{ flex: 1 }}>
        <HeroSection
          image={partOfOurStoryImage}
          title="Your Journey is Part of Our Story"
          subtitle="We'd love to see how you're living life in your Stone Martin home!"
        />

        <Box sx={{ py: { xs: 4, md: 4 }, px: { xs: 2, md: 4 } }}>
          {isLoading && (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <Typography variant="body1">Loading form...</Typography>
            </Box>
          )}
          {isError && (
            <Typography color="error">Failed to load form: {(error as Error).message}</Typography>
          )}
          {form && (
            <HubspotFormRenderer
              formName="Photo Upload"
              form={form}
              onSubmit={handleSubmit}
              isSubmitting={submitMutation.isLoading}
              submitButtonText="Submit Photos"
              formRef={formikRef}
            />
          )}
        </Box>

        {!isLoading && !isError && (
          <Box
            sx={{
              backgroundColor: homeownerPortalTheme.palette.backgroundBlue.main,
              p: 3,
              textAlign: 'center',
              mb: { xs: 0, md: 2 }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: homeownerPortalTheme.palette.primary.dark,
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Terms & Conditions
            </Typography>
          </Box>
        )}
      </Box>

      <SuccessModal open={showSuccessModal} onClose={handleSuccessModalClose} />
    </HomeownerPortalWrapper>
  );
};
